/* @flow */
import React, { Fragment } from 'react';
import classnames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';

// Components
import { Button } from 'dibs-elements/exports/Button';
import Close from 'dibs-vg/dist/react/close';

import type { QuickViewItem_item as QuickViewItemProp } from './__generated__/QuickViewItem_item.graphql';

// Styles
import styles from './styles.scss';

type QuickViewItemProps = {|
    item: QuickViewItemProp,
    isQuickViewActive: boolean,
    onClose: () => void,
    onViewDetailsClick: () => void,
    isMobile?: boolean,
    isTestVariant?: boolean,
|};

type QuickViewItemState = {|
    isQuickViewActive: boolean,
|};

import { FormattedMessage } from 'react-intl';

const copy = {
    by: (
        <FormattedMessage id="dc.searchProductTile.by" defaultMessage="By">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
    materials: (
        <FormattedMessage id="dc.searchProductTile.materials" defaultMessage="Materials">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
    category: (
        <FormattedMessage id="dc.searchProductTile.category" defaultMessage="Category">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),

    viewDetails: (
        <FormattedMessage id="dc.searchProductTile.viewDetails" defaultMessage="View Full Details">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
};

class QuickViewItem extends React.Component<QuickViewItemProps, QuickViewItemState> {
    constructor() {
        super();

        this.state = {
            isQuickViewActive: false,
        };
    }

    componentDidUpdate(prevProps) {
        const { isQuickViewActive } = this.props;
        if (prevProps.isQuickViewActive !== isQuickViewActive) {
            this.setState({ isQuickViewActive });
        }
    }

    render() {
        const { item, onClose, onViewDetailsClick, isMobile, isTestVariant } = this.props;
        const { isQuickViewActive } = this.state;
        /* istanbul ignore if */
        if (!item.quickViewDisplay) {
            return null;
        }

        const pdpUrl = get(item, 'localizedPdpUrl');
        const mobileTitle = get(item, 'quickViewDisplay.mobileTitle');
        const title = get(item, isTestVariant ? 'title' : 'quickViewDisplay.title');
        const paragraphs = get(item, 'quickViewDisplay.paragraphs') || [];
        const quickViewClass = classnames(styles.wrapper, {
            [styles.isActive]: isQuickViewActive,
            [styles.isTestVariant]: isTestVariant,
        });
        const itemClass = classnames(styles.item, {
            [styles.isTestVariant]: isTestVariant,
        });

        const paragraphsMap = paragraphs.reduce((map, paragraph) => {
            const { key, text, mobileText } = paragraph;
            map[key] = isMobile ? mobileText : text;
            return map;
        }, {});

        const { creators, materials, attributes } = paragraphsMap;

        return (
            <div className={quickViewClass}>
                <div className={itemClass}>
                    <div className={styles.close} onClick={onClose}>
                        <Close />
                    </div>
                    {isTestVariant ? (
                        <div className={styles.testContainer}>
                            <div className={styles.testParagraphs}>
                                <div className={styles.testTitle}>
                                    {isMobile ? mobileTitle : title}
                                </div>
                                {creators && (
                                    <div className={styles.testCreator}>
                                        {copy.by} {creators}
                                    </div>
                                )}
                                {attributes && (
                                    <div className={styles.testParagraph}>
                                        {copy.category}
                                        <p>{attributes}</p>
                                    </div>
                                )}
                                {materials && (
                                    <div className={styles.testParagraph}>
                                        {copy.materials}
                                        <p>{materials}</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                {isMobile ? (
                                    <a rel="nofollow" href={pdpUrl} onClick={onViewDetailsClick}>
                                        {copy.viewDetails}
                                    </a>
                                ) : (
                                    <Button
                                        fullWidth
                                        size="small"
                                        type={'secondary'}
                                        href={pdpUrl}
                                        onClick={onViewDetailsClick}
                                    >
                                        {copy.viewDetails}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.content}>
                            <div className={styles.title}>{isMobile ? mobileTitle : title}</div>
                            {!!paragraphs &&
                                paragraphs.map(paragraph => (
                                    <div className={styles.paragraph} key={paragraph.key}>
                                        {isMobile ? paragraph.mobileText : paragraph.text}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default createFragmentContainer(QuickViewItem, {
    item: graphql`
        fragment QuickViewItem_item on Item {
            title
            localizedPdpUrl
            quickViewDisplay {
                title
                mobileTitle: title(formatType: MOBILE)
                paragraphs {
                    key
                    text
                    mobileText: text(formatType: MOBILE)
                }
            }
        }
    `,
});
